import React, { useState } from "react";
import { Box } from "@mui/joy";
import { theme } from "styles/theme";
import CustomModal from "components/common/CustomModal";
import type { TaskEditorState } from "../utils/taskEditorUtils";
import { stableHashBlocks } from "../utils/taskEditorDiff";
import type { AssistantStep, AssistantTurn, Decision } from "./types";
import { ApprovalCard, ApprovalDecisionRow } from "./approval";
import { ReasoningBubble, TextStreamBubble } from "./bubbles";
import { formatDuration, ToolCallRow } from "./toolCallRow";
import { ThinkingIndicator } from "./thinkingIndicator";
import { TodoChecklist } from "./todoChecklist";
import { TurnDiffModal } from "./TurnDiffModal";

// =========================
// TaskPatchPill — 변경사항 보기 / 이 턴 되돌리기 액션 포함
// =========================

interface TaskPatchPillProps {
  // taskBefore 가 있고 현재 task 와 hash 가 달라야 diff 가 의미 있음 (noop 정정).
  taskBefore?: TaskEditorState;
  currentTask?: TaskEditorState;
  onRevert?: () => void;
}

export function TaskPatchPill({
  taskBefore,
  currentTask,
  onRevert,
}: TaskPatchPillProps) {
  const [diffOpen, setDiffOpen] = useState(false);
  const [confirmRevertOpen, setConfirmRevertOpen] = useState(false);
  // hash 동일 = byte-identical noop (서버가 task_changed=true 라 주장해도 실제 변경 없음).
  const hasActualChange =
    !!taskBefore &&
    !!currentTask &&
    stableHashBlocks(taskBefore.blocks) !==
      stableHashBlocks(currentTask.blocks);
  return (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          px: "8px",
          py: "4px",
          fontSize: "11px",
          color: theme.vars.palette.Content.Weak,
        }}
      >
        <Box
          component="span"
          sx={{
            color: theme.vars.palette.primary.solid,
            fontWeight: 700,
          }}
        >
          ✎ 활동지 업데이트됨
        </Box>
        {hasActualChange && (
          <>
            <Box
              component="button"
              type="button"
              onClick={() => setDiffOpen(true)}
              sx={{
                background: "none",
                border: "none",
                padding: 0,
                cursor: "pointer",
                textDecoration: "underline",
                color: theme.vars.palette.Content.Medium,
                fontSize: "11px",
                "&:hover": { color: theme.vars.palette.Content.Strong },
              }}
            >
              이번 턴의 변경 내용
            </Box>
            {onRevert && (
              <Box
                component="button"
                type="button"
                onClick={() => setConfirmRevertOpen(true)}
                sx={{
                  background: "none",
                  border: "none",
                  padding: 0,
                  cursor: "pointer",
                  textDecoration: "underline",
                  color: theme.vars.palette.Content.Medium,
                  fontSize: "11px",
                  "&:hover": { color: "#b91c1c" },
                }}
              >
                되돌리기
              </Box>
            )}
          </>
        )}
      </Box>
      {hasActualChange && taskBefore && currentTask && (
        <TurnDiffModal
          open={diffOpen}
          taskBefore={taskBefore}
          taskAfter={currentTask}
          onClose={() => setDiffOpen(false)}
        />
      )}
      {onRevert && (
        <CustomModal
          open={confirmRevertOpen}
          onClose={() => setConfirmRevertOpen(false)}
          title="되돌리기"
          subtitle="이번 턴의 변경을 적용하기 직전 상태로 되돌릴까요? 이 작업은 취소할 수 없어요."
          actions={[
            {
              label: "되돌리기",
              variant: "Solid",
              color: "danger",
              onClick: () => {
                onRevert();
                setConfirmRevertOpen(false);
              },
            },
            {
              label: "취소",
              variant: "Plain",
              onClick: () => setConfirmRevertOpen(false),
            },
          ]}
        />
      )}
    </>
  );
}

// =========================
// DebugFooter
// =========================

export function DebugFooter({
  debug,
}: {
  debug: NonNullable<AssistantTurn["debug"]>;
}) {
  const copyTrace = () => {
    if (debug.trace_id && typeof navigator !== "undefined") {
      navigator.clipboard?.writeText(debug.trace_id);
    }
  };
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
        mt: "4px",
        fontSize: "10px",
        color: theme.vars.palette.Content.Weak,
        fontFamily:
          "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
      }}
    >
      <Box component="span">⏱ {formatDuration(debug.execution_time_ms)}</Box>
      {debug.trace_id && (
        <Box
          component="span"
          onClick={copyTrace}
          title="클릭해서 trace id 복사"
          sx={{
            cursor: "pointer",
            "&:hover": { color: theme.vars.palette.Content.Medium },
          }}
        >
          · trace {debug.trace_id}
        </Box>
      )}
    </Box>
  );
}

// =========================
// 스텝 그룹화 — DeepAgents `task` 도구(서브에이전트 호출)와 그 내부 도구(subagent 태그)를
// 한 블록으로 묶는다. 활동지(task) 와 구분되는 "subagent" 그룹으로 표면화하기 위함.
// =========================

type ToolStep = Extract<AssistantStep, { kind: "tool" }>;
type TurnRenderItem =
  | { kind: "step"; step: AssistantStep; idx: number }
  | { kind: "subagentGroup"; parent: ToolStep; children: ToolStep[] };

function groupTurnSteps(steps: AssistantStep[]): TurnRenderItem[] {
  const items: TurnRenderItem[] = [];
  for (let i = 0; i < steps.length; i++) {
    const step = steps[i];
    // 부모 `task` 도구: 뒤따르는 subagent 자식 도구들을 흡수해 그룹으로 묶는다.
    if (step.kind === "tool" && step.name === "task" && !step.subagent) {
      const children: ToolStep[] = [];
      let j = i + 1;
      while (j < steps.length) {
        const next = steps[j];
        if (next.kind === "tool" && next.subagent) {
          children.push(next);
          j++;
        } else break;
      }
      items.push({ kind: "subagentGroup", parent: step, children });
      i = j - 1;
      continue;
    }
    items.push({ kind: "step", step, idx: i });
  }
  return items;
}

// =========================
// AssistantTurnView
// =========================

export function AssistantTurnView({
  turn,
  isLast,
  isStreaming,
  currentTask,
  onDecide,
  onRevertTurn,
  onRetry,
}: {
  turn: AssistantTurn;
  isLast: boolean;
  isStreaming: boolean;
  currentTask?: TaskEditorState;
  onDecide: (decisions: Decision[]) => void;
  onRevertTurn?: () => void;
  onRetry?: () => void;
}) {
  // 같은 turn 에서 propose_plan(계획) 승인이 선행됐는지 — update_task 카드의
  // "2/2단계" 표기에 쓰인다.
  const precededByPlan = turn.steps.some(
    (s) =>
      s.kind === "approval_decision" && s.originalAction.name === "propose_plan"
  );
  const turnActive = isStreaming && isLast && turn.status === "streaming";

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "4px",
        animation: "fadeInUp 0.25s ease",
        "@keyframes fadeInUp": {
          from: { opacity: 0, transform: "translateY(4px)" },
          to: { opacity: 1, transform: "translateY(0)" },
        },
      }}
    >
      {groupTurnSteps(turn.steps).map((item) => {
        // 서브에이전트 그룹 — 부모 `task` 도구(verbatim) + 내부 도구를 한 박스로 묶어
        // 활동지(task) 와 시각적으로 구분한다.
        if (item.kind === "subagentGroup") {
          return (
            <Box
              key={item.parent.id}
              sx={{
                borderRadius: "8px",
                border: `1px solid ${theme.vars.palette.Border.Weak}`,
                backgroundColor: theme.vars.palette.Background.Level1,
                py: "2px",
              }}
            >
              <Box
                component="span"
                sx={{
                  display: "block",
                  px: "8px",
                  pt: "2px",
                  fontSize: "10px",
                  letterSpacing: "0.04em",
                  color: theme.vars.palette.Content.Weak,
                  fontFamily:
                    "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                }}
              >
                subagent
              </Box>
              <ToolCallRow step={item.parent} active={turnActive} />
              {item.children.length > 0 && (
                <Box
                  sx={{
                    ml: "20px",
                    pl: "8px",
                    borderLeft: `2px solid ${theme.vars.palette.Border.Weak}`,
                  }}
                >
                  {item.children.map((c) => (
                    <ToolCallRow key={c.id} step={c} active={turnActive} />
                  ))}
                </Box>
              )}
            </Box>
          );
        }
        const { step, idx } = item;
        const isLastStep = idx === turn.steps.length - 1;
        if (step.kind === "tool") {
          // turn 이 실제로 streaming 중일 때만 스피너. 종료된 turn 의 미완료
          // tool(예: 실패한 update_task) 은 정적 마커로 멈춘다.
          const row = (
            <ToolCallRow key={step.id} step={step} active={turnActive} />
          );
          // 그룹에 안 묶인 서브에이전트 도구(방어적) — 들여쓰기 폴백.
          if (step.subagent) {
            return (
              <Box
                key={step.id}
                sx={{
                  ml: "20px",
                  pl: "8px",
                  borderLeft: `2px solid ${theme.vars.palette.Border.Weak}`,
                }}
              >
                {row}
              </Box>
            );
          }
          return row;
        }
        if (step.kind === "text") {
          // 빈/공백만 있는 텍스트 스텝은 빈 버블로 보이므로 렌더하지 않는다.
          if (!step.content.trim()) return null;
          return (
            <TextStreamBubble
              key={step.id}
              step={step}
              isLast={isLast && isLastStep}
              isStreaming={isStreaming && isLast && isLastStep}
            />
          );
        }
        if (step.kind === "reasoning") {
          // 내용 없는 추론 스텝은 'Reasoning' 라벨만 덩그러니 남으므로 렌더하지 않는다.
          if (!step.content.trim()) return null;
          return (
            <ReasoningBubble
              key={step.id}
              step={step}
              isStreaming={turnActive && isLastStep}
            />
          );
        }
        return <ApprovalDecisionRow key={step.id} step={step} />;
      })}

      {turn.todos && turn.todos.length > 0 && (
        <TodoChecklist
          todos={turn.todos}
          active={turnActive}
          failed={turn.status === "error"}
        />
      )}

      {isLast && isStreaming && turn.status === "streaming" && (
        <ThinkingIndicator />
      )}

      {turn.status === "awaiting_approval" && turn.pendingApproval && (
        <ApprovalCard
          data={turn.pendingApproval}
          onDecide={onDecide}
          precededByPlan={precededByPlan}
          baseBlocks={turn.taskBefore?.blocks}
        />
      )}

      {turn.taskChanged && (
        <TaskPatchPill
          taskBefore={turn.taskBefore}
          currentTask={currentTask}
          onRevert={onRevertTurn}
        />
      )}

      {turn.status === "cancelled" && (
        <Box
          sx={{
            fontSize: "12px",
            color: theme.vars.palette.Content.Weak,
            // 좌측 라인을 다른 인라인 상태 행(px:8)과 맞춘다.
            px: "8px",
          }}
        >
          답변을 중단했어요
        </Box>
      )}

      {/* 페이지 이탈/리프레시로 끊긴 미완 turn. checkpoint 가 남아있어 "이어서 하기"
          로 진행 요청을 즉시 보낼 수 있다 (onRetry = "이어서 진행해주세요" 전송).
          이어가기가 불가능한 경우(onRetry 없음 = 마지막 turn 아님)는 "중단됨" 표기도
          숨긴다 — 액션 없는 상태 라벨만 남기지 않는다. */}
      {turn.status === "interrupted" && onRetry && (
        // "✎ 활동지 업데이트됨" pill 과 동일한 외형(11px 컨테이너 · gap 8 · px8 py4)으로
        // 위계·크기를 맞춘다.
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            flexWrap: "wrap",
            px: "8px",
            py: "4px",
            fontSize: "11px",
            color: theme.vars.palette.Content.Weak,
          }}
        >
          {/* 라벨 — ✎ 라벨처럼 11px·700, 색만 테마 주황(palette.text.orange #CC7611).
              ⚠ 로 중단 상태를 아이콘으로 표시. */}
          <Box
            component="span"
            sx={{
              color: theme.vars.palette.text.orange,
              fontWeight: 700,
            }}
          >
            ⚠ 중단됨
          </Box>
          {/* TaskPatchPill 액션과 동일한 밑줄 텍스트 버튼. */}
          <Box
            component="button"
            type="button"
            onClick={onRetry}
            disabled={isStreaming}
            sx={{
              background: "none",
              border: "none",
              padding: 0,
              cursor: "pointer",
              textDecoration: "underline",
              color: theme.vars.palette.Content.Medium,
              fontSize: "11px",
              "&:hover:not(:disabled)": {
                color: theme.vars.palette.Content.Strong,
              },
              "&:disabled": { opacity: 0.5, cursor: "not-allowed" },
            }}
          >
            다시 진행하기
          </Box>
        </Box>
      )}

      {turn.error && (
        <Box
          sx={{
            p: "8px 10px",
            borderRadius: "6px",
            backgroundColor: "rgba(239, 68, 68, 0.08)",
            border: `1px solid rgba(239, 68, 68, 0.3)`,
            fontSize: "12px",
            color: "#b91c1c",
          }}
        >
          {turn.error.message}
        </Box>
      )}

      {turn.error?.type === "timeout" && onRetry && (
        <Box
          component="button"
          type="button"
          onClick={onRetry}
          disabled={isStreaming}
          sx={{
            mt: "6px",
            alignSelf: "flex-start",
            px: "10px",
            py: "5px",
            borderRadius: "6px",
            border: `1px solid ${theme.vars.palette.Border.Strong}`,
            backgroundColor: theme.vars.palette.Surface.Forward,
            cursor: "pointer",
            fontSize: "12px",
            fontWeight: 600,
            color: theme.vars.palette.Content.Strong,
            transition: "background-color 0.12s ease",
            "&:hover:not(:disabled)": {
              backgroundColor: theme.vars.palette.Background.Level1,
            },
            "&:disabled": { opacity: 0.5, cursor: "not-allowed" },
          }}
        >
          다시 시도
        </Box>
      )}

      {turn.debug && <DebugFooter debug={turn.debug} />}
    </Box>
  );
}
